import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { appendCriteriaContains, appendCriteriaEquals, appendCriteriaGreaterThanOrEqual, appendCriteriaLessThanOrEqual, appendPageable, readPagedRows, } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage, beginReportFetch, isStaleReportRequest, isAbortedThunkError } from './reducer.utils';
const initialState = {
    loading: false,
    errorMessage: null,
    report: [],
    totalItems: 0,
    activeRequestId: null,
};
const apiUrl = API_ROUTES.breaks;
function buildBreakReportQuery({ userId, breakReason, dateFrom, dateTo, page = 0, size = 500, }) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, 'breakStartTime,desc');
    appendCriteriaContains(q, 'userId', userId);
    if (breakReason?.trim()) {
        appendCriteriaEquals(q, 'breakReason', breakReason.trim());
    }
    appendCriteriaGreaterThanOrEqual(q, 'breakStartTime', dateFrom);
    appendCriteriaLessThanOrEqual(q, 'breakStartTime', dateTo);
    return q.toString();
}
function filterBreaksByDate(rows, dateFrom, dateTo) {
    if (!dateFrom && !dateTo) {
        return rows;
    }
    const fromMs = dateFrom ? new Date(dateFrom).getTime() : null;
    const toMs = dateTo ? new Date(dateTo).getTime() : null;
    return rows.filter((row) => {
        if (!row.breakStartTime) {
            return false;
        }
        const startMs = new Date(row.breakStartTime).getTime();
        if (Number.isNaN(startMs)) {
            return false;
        }
        if (fromMs != null && startMs < fromMs) {
            return false;
        }
        if (toMs != null && startMs > toMs) {
            return false;
        }
        return true;
    });
}
export async function fetchBreakReportRows(params) {
    const query = buildBreakReportQuery(params);
    const requestUrl = `${apiUrl}?${query}`;
    const response = await axios.get(requestUrl);
    const { rows, total } = readPagedRows(response.data, response.headers);
    const filtered = filterBreaksByDate(rows, params.dateFrom, params.dateTo);
    return { rows: filtered, total: params.dateFrom || params.dateTo ? filtered.length : total };
}
export const getBreakReport = createAsyncThunk('breakReport/fetch', async (params, { signal }) => {
    const query = buildBreakReportQuery(params);
    const requestUrl = `${apiUrl}?${query}`;
    const response = await axios.get(requestUrl, { signal });
    const { rows, total } = readPagedRows(response.data, response.headers);
    const filtered = filterBreaksByDate(rows, params.dateFrom, params.dateTo);
    return { rows: filtered, total: params.dateFrom || params.dateTo ? filtered.length : total };
}, { serializeError: serializeAxiosError });
export const BreakReportSlice = createSlice({
    name: 'breakReport',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addMatcher(isFulfilled(getBreakReport), (state, action) => {
            if (isStaleReportRequest(state, action))
                return;
            state.loading = false;
            state.report = action.payload.rows;
            state.totalItems = action.payload.total;
        })
            .addMatcher(isPending(getBreakReport), (state, action) => {
            beginReportFetch(state, action, () => {
                state.report = [];
            });
        })
            .addMatcher(isRejected(getBreakReport), (state, action) => {
            if (isStaleReportRequest(state, action))
                return;
            if (isAbortedThunkError(action.error))
                return;
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = BreakReportSlice.actions;
export default BreakReportSlice.reducer;
